import React, { useState, useMemo } from 'react'
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  InputAdornment,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Switch,
  List,
  ListItem,
  ListItemText
} from '@mui/material'
import AddIcon from '@mui/icons-material/AddRounded'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import EditIcon from '@mui/icons-material/EditOutlined'
import SearchIcon from '@mui/icons-material/SearchRounded'
import WarningAmberIcon from '@mui/icons-material/WarningAmberRounded'
import { useQuery, useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { GET_ADVANCED_MODIFIERS, GET_RESTAURANT_ITEMS } from '../apollo/advancedMenuQueries'
import {
  CREATE_ADVANCED_MODIFIER,
  UPDATE_ADVANCED_MODIFIER,
  DELETE_ADVANCED_MODIFIER,
  MARK_AS_EIGHTYSIX,
  UNMARK_EIGHTYSIX
} from '../apollo/advancedMenuMutations'
import useBulkSelection, { bulkDelete } from '../components/Common/useBulkSelection'
import { mw, tableCardSx, headCellSx, searchFieldSx, chipSx, pillBtnSx, pillBtnPrimarySx, pillBtnDangerSx, dialogPaperSx, sectionHeaderSx, badgeSx, iconBtnSx, iconBtnDangerSx } from './menuWorkspaceStyles'

// Capitalize the first letter of a modifier name for a consistent look
// (e.g. "bacon" -> "Bacon"). Only the first character is touched so existing
// casing like "BBQ" or "green/red pepper" is preserved after the first letter.
const capitalizeFirst = (s) => {
  const str = (s || '').trim()
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : str
}

const ModifiersSubTab = () => {
  const { t } = useTranslation()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  // Form state
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [modifierType, setModifierType] = useState('OTHER')
  const [isIngredient, setIsIngredient] = useState(true)
  const [isModifier, setIsModifier] = useState(true)
  const [error, setError] = useState('')

  const [warningDialog, setWarningDialog] = useState({ open: false, modifier: null })
  const [untilDateTime, setUntilDateTime] = useState('')

  // Bulk-create state: a small editable table of rows
  const emptyBulkRow = () => ({ name: '', description: '', modifierType: 'OTHER' })
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false)
  const [bulkRows, setBulkRows] = useState([emptyBulkRow()])
  const [bulkError, setBulkError] = useState('')
  const [bulkSaving, setBulkSaving] = useState(false)

  const restaurantId = localStorage.getItem('restaurantId')

  const { data, loading: queryLoading, refetch } = useQuery(GET_ADVANCED_MODIFIERS, {
    variables: { restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'network-only'
  })

  const { data: itemsData } = useQuery(GET_RESTAURANT_ITEMS, {
    variables: { restaurantId },
    skip: !restaurantId
  })

  const refetchQueries = [{ query: GET_ADVANCED_MODIFIERS, variables: { restaurantId } }]

  const [createModifier, { loading: creating }] = useMutation(CREATE_ADVANCED_MODIFIER, {
    refetchQueries,
    onCompleted: () => handleCloseDialog(),
    onError: (err) => setError(err.message)
  })

  // Separate hook for bulk: no auto-close/refetch per call — we close & refetch
  // once after all rows are created.
  const [createModifierBulk] = useMutation(CREATE_ADVANCED_MODIFIER)

  const [deleteModifier] = useMutation(DELETE_ADVANCED_MODIFIER, {
    refetchQueries,
    onError: (err) => {
      console.error('Delete error:', err)
      alert(t('Cannot delete modifier that is in use'))
    }
  })

  const [updateModifier] = useMutation(UPDATE_ADVANCED_MODIFIER, {
    refetchQueries,
    onError: (err) => {
      console.error('Rename error:', err)
      alert(t('Could not rename modifier'))
    }
  })

  const [markAs86] = useMutation(MARK_AS_EIGHTYSIX, {
    refetchQueries,
    onError: (err) => console.error('86 error:', err)
  })

  const [unmark86] = useMutation(UNMARK_EIGHTYSIX, {
    refetchQueries,
    onError: (err) => console.error('Un-86 error:', err)
  })

  const modifiers = data?.advancedModifiers || []

  const filteredModifiers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    const result = q
      ? modifiers.filter(m =>
          m.name.toLowerCase().includes(q) ||
          m.modifierType.toLowerCase().includes(q)
        )
      : modifiers
    return result.slice().sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
  }, [modifiers, searchQuery])

  const resetForm = () => {
    setName('')
    setDescription('')
    setModifierType('OTHER')
    setIsIngredient(true)
    setIsModifier(true)
    setError('')
  }

  const handleOpenCreate = () => {
    resetForm()
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    resetForm()
  }

  const handleSubmit = () => {
    setError('')
    if (!name.trim()) {
      setError(t('Modifier name is required'))
      return
    }

    createModifier({
      variables: {
        restaurantId,
        input: {
          name: capitalizeFirst(name),
          description: description.trim(),
          modifierType,
          isIngredient,
          isModifier
        }
      }
    })
  }

  const handleDelete = (e, modifierId) => {
    e.stopPropagation()
    if (window.confirm(t('Are you sure? This will remove it from all groups!'))) {
      deleteModifier({ variables: { id: modifierId } })
    }
  }

  // Rename a modifier in place. Preserves its other fields (type/description/
  // ingredient flags) and just swaps the name. The new name propagates to every
  // group that uses this modifier, since they reference it by id.
  const handleRename = (e, mod) => {
    e.stopPropagation()
    const name = window.prompt(t('Rename modifier:'), mod.name)
    if (name === null) return
    const trimmed = capitalizeFirst(name)
    if (!trimmed || trimmed === mod.name) return
    updateModifier({
      variables: {
        id: mod._id,
        input: {
          name: trimmed,
          description: mod.description || null,
          modifierType: mod.modifierType || 'OTHER',
          isIngredient: !!mod.isIngredient,
          isModifier: mod.isModifier !== false,
        },
      },
    })
  }

  const bulk = useBulkSelection(filteredModifiers.map(m => m._id))

  const handleBulkDelete = async () => {
    const ids = bulk.selectedIds
    if (ids.length === 0) return
    if (!window.confirm(t('Delete {{n}} modifiers? They will be removed from all groups.', { n: ids.length }))) return
    const { failed } = await bulkDelete(ids, (id) => deleteModifier({ variables: { id } }))
    bulk.clear()
    if (failed > 0) setError(t('{{n}} modifiers could not be deleted (they may be in use).', { n: failed }))
  }

  // ── Bulk create ──────────────────────────────────────────────────────────
  const handleOpenBulk = () => {
    setBulkRows([emptyBulkRow()])
    setBulkError('')
    setBulkDialogOpen(true)
  }

  const handleCloseBulk = () => {
    setBulkDialogOpen(false)
    setBulkRows([emptyBulkRow()])
    setBulkError('')
  }

  const updateBulkRow = (idx, field, value) => {
    setBulkRows(rows => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)))
  }

  const addBulkRow = () => setBulkRows(rows => [...rows, emptyBulkRow()])

  const removeBulkRow = (idx) => {
    setBulkRows(rows => (rows.length === 1 ? rows : rows.filter((_, i) => i !== idx)))
  }

  const handleBulkSubmit = async () => {
    setBulkError('')
    const rows = bulkRows.map(r => ({ ...r, name: capitalizeFirst(r.name), description: r.description.trim() }))
    const valid = rows.filter(r => r.name)
    if (valid.length === 0) {
      setBulkError(t('Add at least one modifier name'))
      return
    }
    setBulkSaving(true)
    try {
      await Promise.all(valid.map(r =>
        createModifierBulk({
          variables: {
            restaurantId,
            input: {
              name: r.name,
              description: r.description,
              modifierType: r.modifierType,
              isIngredient: true,
              isModifier: true
            }
          }
        })
      ))
      await refetch()
      handleCloseBulk()
    } catch (err) {
      setBulkError(err.message || t('Failed to create some modifiers'))
    } finally {
      setBulkSaving(false)
    }
  }

  const handleToggle = (mod) => {
    const is86ed = mod.libraryStatus?.is86ed
    if (is86ed) {
      // Turn ON — no warning needed
      unmark86({
        variables: { targetType: 'MODIFIER', targetId: mod._id }
      })
    } else {
      // Turn OFF — show warning dialog first
      setWarningDialog({ open: true, modifier: mod })
    }
  }

  const handleConfirmTurnOff = (duration) => {
    const mod = warningDialog.modifier
    let until = null
    if (duration === 'today') { const m = new Date(); m.setHours(23,59,59,0); until = m.toISOString() }
    else if (duration === '2h') until = new Date(Date.now() + 2*60*60*1000).toISOString()
    else if (duration === '4h') until = new Date(Date.now() + 4*60*60*1000).toISOString()
    markAs86({
      variables: {
        restaurantId,
        input: {
          targetType: 'MODIFIER',
          targetId: mod._id,
          reason: 'Out of stock',
          until
        }
      }
    })
    setWarningDialog({ open: false, modifier: null })
    setUntilDateTime('')
  }

  const handleCancelWarning = () => {
    setWarningDialog({ open: false, modifier: null })
    setUntilDateTime('')
  }

  const typeLabel = (type) => type.replace(/_/g, ' ')

  // Compute warning impact whenever the dialog is open
  const warningMod = warningDialog.modifier
  const warningGroupCount = warningMod?.libraryStatus?.usageCount ?? 0
  const warningAffectedItems = warningMod
    ? (itemsData?.advancedRestaurantItems || []).filter(item =>
        item.criticalIngredients?.some(ci => ci._id === warningMod._id)
      )
    : []

  return (
    <Box>
      <Box sx={sectionHeaderSx}>
        <Typography variant="h4" fontWeight="bold" sx={{ color: mw.color.textPrimary }}>
          {t('Modifiers')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          {bulk.selectedIds.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleBulkDelete}
              sx={pillBtnDangerSx}
            >
              {t('Delete Selected')} ({bulk.selectedIds.length})
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleOpenBulk}
            sx={pillBtnSx}
          >
            {t('Bulk Create')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            sx={pillBtnPrimarySx}
          >
            {t('Create Modifier')}
          </Button>
        </Box>
      </Box>

      <Box sx={{ mb: 2.5, maxWidth: 380 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={t('Search modifiers...')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: mw.color.textFaint }} />
              </InputAdornment>
            )
          }}
          sx={searchFieldSx}
        />
      </Box>

      {/* Modifier Library Table */}
      <TableContainer component={Paper} sx={tableCardSx}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: mw.color.headBg }}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={bulk.allSelected}
                  indeterminate={bulk.someSelected}
                  onChange={bulk.toggleAll}
                  onClick={(e) => e.stopPropagation()}
                />
              </TableCell>
              <TableCell sx={headCellSx}>{t('Name')}</TableCell>
              <TableCell sx={headCellSx}>{t('Type')}</TableCell>
              <TableCell sx={headCellSx} align="center">{t('Used In')}</TableCell>
              <TableCell sx={headCellSx} align="center">{t('Status')}</TableCell>
              <TableCell sx={headCellSx} align="right" width={80}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {queryLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredModifiers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6, color: mw.color.textMuted }}>
                  {modifiers.length === 0
                    ? t('No modifiers yet. Click "Create Modifier" to get started.')
                    : t('No modifiers match your search.')}
                </TableCell>
              </TableRow>
            ) : (
              filteredModifiers.map((mod) => {
                const isAvailable = !mod.libraryStatus?.is86ed
                return (
                  <TableRow key={mod._id} hover sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={bulk.isSelected(mod._id)}
                        onChange={() => bulk.toggle(mod._id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body1"
                        fontWeight={500}
                        sx={{ color: isAvailable ? mw.color.textPrimary : mw.color.textFaint, fontWeight: 700, fontSize: 15, textDecoration: isAvailable ? 'none' : 'line-through' }}
                      >
                        {capitalizeFirst(mod.name)}
                      </Typography>
                      {mod.description && (
                        <Typography variant="caption" sx={{ color: mw.color.textMuted, fontSize: 13, display: 'block' }}>
                          {mod.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={typeLabel(mod.modifierType)}
                        size="small"
                        sx={badgeSx.neutral}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {(() => {
                        const names = mod.libraryStatus?.usageGroups || []
                        if (names.length === 0) {
                          return <Typography variant="body2" sx={{ color: mw.color.textFaint }}>—</Typography>
                        }
                        const MAX = 3
                        const shown = names.slice(0, MAX)
                        const extra = names.length - shown.length
                        return (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'center' }}>
                            {shown.map((name) => (
                              <Chip key={name} size="small" label={name}
                                sx={chipSx.modifier} />
                            ))}
                            {extra > 0 && (
                              <Tooltip title={names.slice(MAX).join(', ')}>
                                <Chip size="small" label={`+${extra}`}
                                  sx={chipSx.overflow} />
                              </Tooltip>
                            )}
                          </Box>
                        )
                      })()}
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Switch
                            checked={isAvailable}
                            onChange={() => handleToggle(mod)}
                            size="small"
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: '#fff' },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: mw.color.on }
                            }}
                          />
                          <Typography
                            variant="caption"
                            fontWeight={500}
                            sx={{ color: isAvailable ? mw.color.onText : mw.color.off, fontWeight: 700, fontSize: 12, letterSpacing: '0.04em', minWidth: 26 }}
                          >
                            {isAvailable ? t('ON') : t('OFF')}
                          </Typography>
                        </Box>
                        {!isAvailable && mod.libraryStatus?.eightysixUntil && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: mw.color.sky,
                              fontSize: 11,
                              fontWeight: 600,
                              bgcolor: mw.color.skySoft,
                              border: `1px solid ${mw.color.skyBorder}`,
                              borderRadius: mw.radius.badge,
                              px: 0.75,
                              py: 0.25,
                              textAlign: 'center'
                            }}
                          >
                            ⏰ {t('ON at')} {new Date(mod.libraryStatus.eightysixUntil).toLocaleString()}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', pl: 1 }}>
                      <Tooltip title={t('Rename')}>
                        <IconButton size="small" onClick={(e) => handleRename(e, mod)} sx={iconBtnSx}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('Delete')}>
                        <IconButton size="small" color="error" onClick={(e) => handleDelete(e, mod._id)} sx={iconBtnDangerSx}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Turn Off Warning Dialog */}
      <Dialog
        open={warningDialog.open}
        onClose={handleCancelWarning}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: mw.color.textPrimary, fontWeight: 'bold' }}>
          <WarningAmberIcon sx={{ color: mw.color.accent, fontSize: 28 }} />
          {t('Turn Off')} &ldquo;{warningMod?.name}&rdquo;?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: mw.color.textSecondary, mb: 2 }}>
            {t('Please review the impact before turning this modifier off:')}
          </Typography>

          {warningAffectedItems.length > 0 && (
            <Box sx={{ mb: 2, bgcolor: mw.color.amberSoft, border: `1px solid ${mw.color.amberBorder}`, borderRadius: mw.radius.sm, p: 2 }}>
              <Typography variant="body2" fontWeight="bold" sx={{ color: mw.color.amber, mb: 1 }}>
                ⚠ {t('The following items will be automatically turned OFF')}
              </Typography>
              <Typography variant="caption" sx={{ color: mw.color.textMuted, display: 'block', mb: 1 }}>
                {t('Because this modifier is tagged as a critical ingredient in these items:')}
              </Typography>
              <List dense disablePadding>
                {warningAffectedItems.map(item => (
                  <ListItem key={item._id} sx={{ py: 0.25, px: 0 }}>
                    <ListItemText
                      primary={`• ${item.name}`}
                      primaryTypographyProps={{ variant: 'body2', sx: { color: mw.color.textPrimary, fontWeight: 600 } }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {warningGroupCount > 0 && (
            <Box sx={{ bgcolor: mw.color.skySoft, border: `1px solid ${mw.color.skyBorder}`, borderRadius: mw.radius.sm, p: 2 }}>
              <Typography variant="body2" fontWeight="bold" sx={{ color: mw.color.sky, mb: 0.5 }}>
                ℹ {t('Used in')} {warningGroupCount} {t('modifier group(s)')}
              </Typography>
              <Typography variant="caption" sx={{ color: mw.color.textMuted }}>
                {t("Customers won't be able to select this modifier from those groups.")}
              </Typography>
            </Box>
          )}

          {warningAffectedItems.length === 0 && warningGroupCount === 0 && (
            <Typography variant="body2" sx={{ color: mw.color.textMuted }}>
              {t('This modifier is not currently linked to any items or groups.')}
            </Typography>
          )}

          {/* Duration picker */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" fontWeight="bold" sx={{ color: mw.color.textPrimary, mb: 1.5 }}>
              ⏰ {t('How long should this modifier be turned off?')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[
                { key: 'today',  label: '🌙 Rest of today',          sub: t('Auto turns ON at midnight') },
                { key: '2h',    label: '⏱ For 2 hours',             sub: t('Auto turns ON in 2 hours') },
                { key: '4h',    label: '⏱ For 4 hours',             sub: t('Auto turns ON in 4 hours') },
                { key: 'manual',label: '🔒 Until I turn it back on', sub: t('Stays OFF until manually turned ON') },
              ].map(opt => (
                <Button
                  key={opt.key}
                  variant="outlined"
                  onClick={() => handleConfirmTurnOff(opt.key)}
                  sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 1.2, px: 2, borderRadius: mw.radius.sm, borderColor: mw.color.border, '&:hover': { borderColor: mw.color.danger, bgcolor: mw.color.dangerSoft } }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{opt.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{opt.sub}</Typography>
                  </Box>
                </Button>
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelWarning} variant="outlined" sx={pillBtnSx}>
            {t('Cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Modifier Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ fontWeight: 'bold', color: mw.color.textPrimary }}>
          {t('Create Modifier')}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <TextField
            autoFocus
            fullWidth
            label={t('Modifier Name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            margin="normal"
            required
            placeholder={t('e.g., Extra Cheese, Mushrooms')}
          />

          <TextField
            fullWidth
            label={t('Description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            margin="normal"
            multiline
            rows={2}
            placeholder={t('Optional description')}
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>{t('Type')}</InputLabel>
            <Select
              value={modifierType}
              onChange={(e) => setModifierType(e.target.value)}
              label={t('Type')}
            >
              <MenuItem value="INGREDIENT">{t('Ingredient')}</MenuItem>
              <MenuItem value="TOPPING">{t('Topping')}</MenuItem>
              <MenuItem value="SAUCE">{t('Sauce')}</MenuItem>
              <MenuItem value="CUSTOMIZATION">{t('Customization')}</MenuItem>
              <MenuItem value="SIZE">{t('Size')}</MenuItem>
              <MenuItem value="TEMPERATURE">{t('Temperature')}</MenuItem>
              <MenuItem value="SPICE_LEVEL">{t('Spice Level')}</MenuItem>
              <MenuItem value="OTHER">{t('Other')}</MenuItem>
            </Select>
          </FormControl>

        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={creating} sx={pillBtnSx}>
            {t('Cancel')}
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={creating} sx={{ ...pillBtnPrimarySx, minWidth: 96 }}>
            {creating ? <CircularProgress size={20} /> : t('Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Create Dialog — editable table of modifiers */}
      <Dialog open={bulkDialogOpen} onClose={handleCloseBulk} maxWidth="md" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ fontWeight: 'bold', color: mw.color.textPrimary }}>
          {t('Bulk Create Modifiers')}
        </DialogTitle>
        <DialogContent>
          {bulkError && <Alert severity="error" sx={{ mb: 2 }}>{bulkError}</Alert>}
          <Typography variant="body2" sx={{ color: mw.color.textMuted, mb: 2 }}>
            {t('Enter multiple modifiers below and create them all at once. Rows with an empty name are skipped.')}
          </Typography>
          <TableContainer component={Paper} sx={{ boxShadow: 'none', border: `1px solid ${mw.color.border}`, borderRadius: mw.radius.sm }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: mw.color.headBg }}>
                  <TableCell sx={{ ...headCellSx, width: '32%' }}>{t('Name')} *</TableCell>
                  <TableCell sx={{ ...headCellSx, width: '38%' }}>{t('Description')}</TableCell>
                  <TableCell sx={{ ...headCellSx, width: '22%' }}>{t('Type')}</TableCell>
                  <TableCell sx={{ width: '8%' }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {bulkRows.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <TextField
                        fullWidth size="small" variant="standard"
                        placeholder={t('e.g., Extra Cheese')}
                        value={row.name}
                        onChange={(e) => updateBulkRow(idx, 'name', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth size="small" variant="standard"
                        placeholder={t('Optional')}
                        value={row.description}
                        onChange={(e) => updateBulkRow(idx, 'description', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        fullWidth size="small" variant="standard"
                        value={row.modifierType}
                        onChange={(e) => updateBulkRow(idx, 'modifierType', e.target.value)}
                      >
                        <MenuItem value="INGREDIENT">{t('Ingredient')}</MenuItem>
                        <MenuItem value="TOPPING">{t('Topping')}</MenuItem>
                        <MenuItem value="SAUCE">{t('Sauce')}</MenuItem>
                        <MenuItem value="CUSTOMIZATION">{t('Customization')}</MenuItem>
                        <MenuItem value="SIZE">{t('Size')}</MenuItem>
                        <MenuItem value="TEMPERATURE">{t('Temperature')}</MenuItem>
                        <MenuItem value="SPICE_LEVEL">{t('Spice Level')}</MenuItem>
                        <MenuItem value="OTHER">{t('Other')}</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={t('Remove row')}>
                        <span>
                          <IconButton
                            size="small" color="error"
                            disabled={bulkRows.length === 1}
                            onClick={() => removeBulkRow(idx)}
                            sx={iconBtnDangerSx}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Button
            startIcon={<AddIcon />}
            onClick={addBulkRow}
            sx={{ mt: 1.5, textTransform: 'none', fontWeight: 600, color: mw.color.accent }}
          >
            {t('Add row')}
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulk} disabled={bulkSaving} sx={pillBtnSx}>
            {t('Cancel')}
          </Button>
          <Button variant="contained" onClick={handleBulkSubmit} disabled={bulkSaving} sx={{ ...pillBtnPrimarySx, minWidth: 110 }}>
            {bulkSaving ? <CircularProgress size={20} /> : t('Create All')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ModifiersSubTab
